import React, {useEffect, useState, useCallback} from "react";
import translate from "components/translations/translations.wrapper";
import {autoMenuStyle} from "utils/reactSelectTheme";
import {UserSearch} from "components/input/UserSearch.component";
import SettingsPreferencesCard from "components/newSettings/Settings.preferences.card";
import {Controller, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {currentPreferenceSelector} from "components/settings/preferences.selector";
import {SubstituteActivePeriod} from "types/nextTask";
import DateFilter from "components/input/CalendarInput.component";
import moment from "moment";
import * as reduxSelectors from "components/store/application.reducers.js";
import {Form} from "react-bootstrap";
import debounce from "lodash/debounce";
import * as Api from "utils/api/api";
import ErrorInContext from "components/common/errorInContext";

export const SubstituteContent = (props: any): React.ReactElement => {
    const settings = useSelector(currentPreferenceSelector);
    const context = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userLanguage = useSelector(reduxSelectors.getLanguage);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<{ title: string, text: string, more?: string | null } | null>(null);

    const initializeSubstituteSettings = (substitute: any) => {
        const object = {
            ...substitute,
            from: moment(substitute.from),
            to: moment(substitute.to),
        }
        if (!substitute.active) {
            object.activeOption = SubstituteActivePeriod.INACTIVE;
            object.from = moment();
            object.to = moment();
        } else {
            object.activeOption = substitute.to ? SubstituteActivePeriod.ACTIVE_PERIOD : SubstituteActivePeriod.ACTIVE_NOW;
        }
        return object;
    }

    const [newSubstituteSettings, setNewSubstituteSettings] = useState(initializeSubstituteSettings(settings.substitute));

    const {control, handleSubmit} = useForm({
        defaultValues: {
            ...settings.substitute
        },
    });

    const debouncedSubmit = useCallback(
        debounce((data: any) => {
            handleSubmit(onSubmit)(data);
        }, 1000),
        [handleSubmit]
    );

    useEffect(() => {
        setNewSubstituteSettings(initializeSubstituteSettings(settings.substitute));
    }, [context]);

    useEffect(() => {
        return () => {
            debouncedSubmit.cancel();
        };
    }, [debouncedSubmit]);

    const changeUser = (value: any) => {
        console.log("here...."+value?.substituteId + " - " + newSubstituteSettings.substituteId);
        if (value?.substituteId === newSubstituteSettings.substituteId) return;
        const updatedSettings = {
            ...newSubstituteSettings,
            active: true,
            from: new Date(),
            substituteName: value?.label || null,
            substituteId: value?.value || null,
        };

        setNewSubstituteSettings(updatedSettings);
        debouncedSubmit(updatedSettings);
    };

    const changeActiveOption = (value: SubstituteActivePeriod) => {
        const updatedSettings = {
            ...newSubstituteSettings,
            activeOption: value,
            active: value !== SubstituteActivePeriod.INACTIVE,
            from: value === SubstituteActivePeriod.ACTIVE_PERIOD ? moment() : newSubstituteSettings.from,
            to: value === SubstituteActivePeriod.ACTIVE_PERIOD ? newSubstituteSettings.to : moment(),
        };

        setNewSubstituteSettings(updatedSettings);
        debouncedSubmit(updatedSettings);
    };

    const changeDates = (date: Date, type: "from" | "to") => {
        const updatedSettings = {...newSubstituteSettings, [type]: moment(date)};

        if (type === "from" && date > newSubstituteSettings.to) {
            updatedSettings.to = moment(date);
        }
        setNewSubstituteSettings(updatedSettings);
        debouncedSubmit(updatedSettings);
    };

    const onSubmit = async (data: any, updatedSettings: any) => {
        if (loading) {
            return;
        }

        setLoading(true);

        const dataToSend = {
            ...settings,
            substitute: {
                ...settings.substitute,
                substituteId: updatedSettings.substituteId,
                substituteName: updatedSettings.substituteName,
                active: updatedSettings.activeOption !== SubstituteActivePeriod.INACTIVE,
                from: updatedSettings.activeOption === SubstituteActivePeriod.ACTIVE_PERIOD ? moment(updatedSettings.from).hours(12).format("YYYY-MM-DD") : moment().hours(12).format("YYYY-MM-DD"),
                to: updatedSettings.activeOption === SubstituteActivePeriod.ACTIVE_PERIOD ? moment(updatedSettings.to).hours(12).format("YYYY-MM-DD") : null,
            }
        };

        try {
            await Api.updateUserSettings(dataToSend, context);
        } catch (error) {
            setError({
                title: props.translate("settings.preferences.saveErrorTitle"),
                text: props.translate("settings.preferences.saveErrorText"),
                more: error.errorMessages || null
            });

            Api.getUserData();
        } finally {
            setLoading(false);
        }
    };

    const lang = moment().locale(userLanguage === "en" ? "en-gb" : userLanguage);
    const valueFormatter = lang.localeData()._longDateFormat.L;

    const renderDateController = (name: string, minDate: any, selectedValue: Date, onChange: (value: Date) => void) => {
        const today = moment().startOf('day');
        const adjustedValue = moment(selectedValue).isBefore(today) ? today.toDate() : moment(selectedValue).hours(12);

        return (
            <Controller
                name={name}
                control={control}
                render={({field: {onBlur}}) => (
                    <DateFilter
                        style="popup"
                        isDisabled={newSubstituteSettings.activeOption !== SubstituteActivePeriod.ACTIVE_PERIOD}
                        translate={props.translate}
                        onBlur={onBlur}
                        format={valueFormatter}
                        userLanguage={userLanguage}
                        minDate={minDate}
                        selectedValue={adjustedValue}
                        onChange={onChange}
                        testid={`__tst_${name}`}
                    />
                )}
            />
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {error && <ErrorInContext {...error}
                                      onCloseCallback={() => {
                                          setError(null);
                                      }}
            />}

            <SettingsPreferencesCard
                title="My substitute for all document types"
                subtitle="Select user"
                contentInput={
                    <div className="col-md-5 mx-0 px-0 ast_user-search">
                        <UserSearch
                            className="col-md-12 mx-0 px-0"
                            propagateValue={changeUser}
                            value={{
                                label: newSubstituteSettings.substituteName,
                                value: newSubstituteSettings.substituteId,
                            }}
                            placeholder={props.translate("settings.substitute.selectUser")}
                            clearable
                            styles={autoMenuStyle}
                            ariaLabel={
                                newSubstituteSettings.substituteName ||
                                props.translate("settings.substitute.selectUser")
                            }
                            translate={props.translate}
                        />

                        {Object.values(SubstituteActivePeriod).map((option) => (
                            <div className={"d-flex flex-column"} key={option}>
                                <Form.Check
                                    name="activeOption"
                                    value={option}
                                    id={option}
                                    type="radio"
                                    className="radio"
                                    disabled={!newSubstituteSettings.substituteId}
                                    checked={newSubstituteSettings.activeOption === option}
                                    onChange={() => changeActiveOption(option)}
                                    aria-label={
                                        props.translate("settings.substitute.afterHandle ") +
                                        props.translate(`settings.substitute.${option}`)
                                    }
                                    label={
                                        <span className={"font-16"}>
                                            {props.translate(`settings.substitute.${option}`)}
                                        </span>
                                    }
                                    data-testid={`__tst_${option}`}
                                />
                            </div>
                        ))}

                        <div className="d-flex flex-row justify-content-between mt-2">
                            {renderDateController("from", moment(), newSubstituteSettings.from.toDate(), (value) => changeDates(value, "from"))}
                            {renderDateController("to", newSubstituteSettings.from.toDate(), newSubstituteSettings.to.toDate(), (value) => changeDates(value, "to"))}
                        </div>
                    </div>
                }
            />
        </form>
    );
};

const withTranslations = translate(SubstituteContent);
export default withTranslations;